import User from "@/Layouts/User";
import Head from "next/head";
import { useBuy } from "@/context/buy";
import { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import { useRouter } from "next/router";
import { auth, RecaptchaVerifier } from "@/firebase";
import { encryptInteger } from "@/Utils/encryptInteger";
import { useLoading } from "@/context/loading";
import MetaTags from "@/Components/SEO/MetaTags";
import { Schema } from "@/Components/SEO/Schema";
import withBaseUrl from "@/Utils/const";
import { motion } from "framer-motion";
import { formatLongDateTime } from "@/Utils/dateFormat";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "@/Components/User/Modal/CheckoutForm";
import { loadStripe } from "@stripe/stripe-js";

export default function index() {
    const router = useRouter();
    const { isLoading } = useLoading();
    const { setOpenModal } = useBuy();
    const [stripeElements, setStripeElements] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showData, setShowData] = useState();
    const [paymentLoading, setPaymentLoading] = useState(false);

    useEffect(() => {
        let loggedInUser = false;
        let encryptedData = localStorage.getItem("user_data");
        if (encryptedData) {
            loggedInUser = true;
            router.back();
        }

        setShowData(JSON.parse(sessionStorage.getItem("show_data")));
    }, []);

    const formik = useFormik({
        initialValues: {
            email: "",
            first_name: "",
            last_name: "",
            phone_number: "",
            country_code: "+1",
            agree: false,
            is_sms_on: false,
        },

        validationSchema: Yup.object({
            email: Yup.string()
                .email("Invalid email format")
                .required("Email is required"),
            first_name: Yup.string().required("First Name is required"),
            last_name: Yup.string().required("Last Name is required"),
            phone_number: Yup.string()
                .matches(/^[0-9]+$/, "Phone number must contain only digits")
                .min(10, "Phone number must be at least 10 digits")
                .required("Phone Number is required"),
            agree: Yup.boolean().oneOf([true], "You must agree to the terms"),
        }),

        onSubmit: async (values) => {
            setIsSubmitting(true);

            try {

                if (!window.recaptchaVerifier) {
                    window.recaptchaVerifier = new RecaptchaVerifier(
                        auth,
                        "submit-button-id",
                        {
                            size: "invisible",
                            callback: () => {
                                console.log("reCAPTCHA verified");
                            },
                            "expired-callback": () => {
                                window.recaptchaVerifier = null;
                                toast.error("reCAPTCHA expired. Please try again.");
                            },
                        }
                    );
                }

                try {
                    await window.recaptchaVerifier.render();
                } catch (error) {
                    console.error("reCAPTCHA render error:", error);
                    window.recaptchaVerifier = null;
                    toast.error("Error initializing reCAPTCHA. Please try again.");
                    return;
                }
                const formData = new FormData();
                let cartData = JSON.parse(
                    localStorage.getItem(`qoncert_cart_${showData?.id}`)
                );
                const rawPhoneNumber = values.phone_number;
                let referrals = cartData?.map(({ user_id, ...rest }) => rest);
                const totalTickets = cartData.reduce((sum, item) => sum + item.ticket_count, 0);
                formData.append("show_id", showData?.id);
                formData.append("num_tickets", totalTickets);
                formData.append("email", values.email);
                formData.append("name", `${values.first_name} ${values.last_name}`);
                formData.append("phone_number", rawPhoneNumber);
                formData.append("country_code", values.country_code);
                formData.append("is_sms_on", values.is_sms_on ? 1 : 0);
                formData.append("agree", values.agree ? 1 : 0);
                formData.append("referrals", JSON.stringify(referrals));

                const response = await fetch(
                    `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageBooking`,
                    {
                        method: "POST",
                        body: formData,
                    }
                );

                const data = await response.json();

                if (data.status) {
                    const encryptedJson = encryptInteger(JSON.stringify(data.data));
                    localStorage.setItem("payment_data", encryptedJson);
                    setPaymentLoading(true);
                    loadStripePayment(data.data);
                } else {
                    toast.error(data.message || "Failed to create account");
                }
            } catch (error) {
                console.error("Form submission error:", error);
                toast.error("Something went wrong. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const loadStripePayment = async (data) => {
        try {
            const stripePromise = await loadStripe(data.publishableKey);

            const options = {
                clientSecret: data.paymentIntent,
                appearance: {
                    theme: "night",
                    variables: {
                        colorPrimary: "#FA2CB4",
                        colorBackground: "#0D0C0F",
                        colorText: "#FFFFFF",
                        colorDanger: "#ff3860",
                        borderRadius: "8px",
                    },
                },
                layout: {
                    type: "tabs",
                    defaultCollapsed: false,
                },
            };

            const elements = (
                <Elements stripe={stripePromise} options={options}>
                    <CheckoutForm />
                </Elements>
            );

            setStripeElements(elements);
            setTimeout(() => {
                setPaymentLoading(false);
            }, 1000);
        } catch (error) {
            console.error("Error in loadStripePayment:", error);
        }
    };

    const getBreadcrumbs = () => {
        return [
            {
                name: "Home",
                url: withBaseUrl("/"),
            },
            {
                name: "Account",
                url: withBaseUrl("/register"),
            },
        ];
    };

    const formatDateInCustomTimezone = (show_date, show_time) => {
        return formatLongDateTime(show_date, show_time);
    };

    return (
        <User>
            <MetaTags
                title="Register | Qoncert"
                description="Create your Qoncert account to book tickets, manage shows, and connect with artists."
                ogImage="/assets/logo.webp"
                canonicalUrl="/register"
                noindex={true}
            />
            <Head>
                <title>Register | Qoncert</title>
                <Schema breadcrumbs={getBreadcrumbs()} />
            </Head>
            {stripeElements ? (
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    className="flex justify-center  items-center fixed inset-0  overflow-y-auto bg-gray-700/50 z-[9999999]"
                >
                    <div className="bg-[#0D0D10] h-fit w-[90%]  min-h-[300px]  md:mt-[50px] lg:mb-[50px] py-[36px] px-[18px] lg:p-12 rounded-lg md:w-1/3">
                        {paymentLoading ? (
                            <div className="flex h-[270px] w-full items-center justify-center">
                                <div className="loader"></div>
                            </div>
                        ) : (
                            stripeElements
                        )}
                    </div>
                </div>
            ) : (
                <div
                    id="CreateUserDetailsModal"
                    className="font-urbanist py-10 lg:py-20 "
                >
                    <div className="layout_bg_image hidden lg:block">
                        <span className="bg_inner_shadow"></span>
                    </div>
                    <div
                        className="flex flex-col md:flex-row justify-start overflow-auto lg:items-center lg:justify-center   h-full inset-0 px-7 md:px-0"
                        onClick={() =>
                            setOpenModal((prev) => ({
                                ...prev,
                                create: false,
                            }))
                        }
                    >
                        <motion.div
                            id="login-modal"
                            initial={{ opacity: 0, y: 20 }}
                            animate={!isLoading ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="bg-[#17191d] mt-6 mb-20 relative h-fit w-full max-w-[650px] md:my-[20px] overflow-y-auto overflow-x-hidden px-[20px] pb-[40px] rounded-[16px] lg:rounded-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                                className="hidden w-full lg:block"
                            >
                                <div className="breadcrumbs w-full max-w-[500px] mx-auto pt-10">
                                    <div
                                        className={`flex gap-4 items-center bread-item active-bread`}
                                    >
                                        <p>Details</p>
                                    </div>
                                    <div className="bread-icons gap-3 flex items-center">
                                        <svg
                                            className={` active-bread `}
                                            width="25"
                                            height="1"
                                            viewBox="0 0 25 1"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <line
                                                y1="0.5"
                                                x2="25"
                                                y2="0.5"
                                                stroke="#FA2CB4"
                                            />
                                        </svg>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g clipPath="url(#clip0_1708_9606)">
                                                <path
                                                    d="M12 22C6.47699 22 2 17.523 2 12C2 6.477 6.47699 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM11.003 16L18.073 8.929L16.659 7.515L11.003 13.172L8.17399 10.343L6.75999 11.757L11.003 16Z"
                                                    fill="#9E9E9E"
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_1708_9606">
                                                    <rect width="24" height="24" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <svg
                                            width="25"
                                            height="1"
                                            viewBox="0 0 25 1"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <line
                                                y1="0.5"
                                                x2="25"
                                                y2="0.5"
                                                stroke="#9E9E9E"
                                            />
                                        </svg>
                                    </div>
                                    <div
                                        className={`flex gap-4 items-center bread-item`}
                                    >
                                        <p>Payment</p>
                                    </div>
                                    <div className="bread-icons gap-3 flex items-center">
                                        <svg
                                            width="25"
                                            height="1"
                                            viewBox="0 0 25 1"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <line
                                                y1="0.5"
                                                x2="25"
                                                y2="0.5"
                                                stroke="#9E9E9E"
                                            />
                                        </svg>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g clipPath="url(#clip0_1708_9606)">
                                                <path
                                                    d="M12 22C6.47699 22 2 17.523 2 12C2 6.477 6.47699 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM11.003 16L18.073 8.929L16.659 7.515L11.003 13.172L8.17399 10.343L6.75999 11.757L11.003 16Z"
                                                    fill="#9E9E9E"
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_1708_9606">
                                                    <rect width="24" height="24" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <svg
                                            width="25"
                                            height="1"
                                            viewBox="0 0 25 1"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <line
                                                y1="0.5"
                                                x2="25"
                                                y2="0.5"
                                                stroke="#9E9E9E"
                                            />
                                        </svg>
                                    </div>
                                    <div
                                        className={`flex gap-4 items-center bread-item`}
                                    >
                                        <p>Confirmation</p>
                                    </div>
                                </div>
                            </motion.div>
                            <div className="mt-6 lg:mt-0 md:px-8">
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                                    transition={{ duration: 0.5, delay: 0.4 }}
                                    className="lg:mt-7 createeAcc text-xl"
                                >
                                    <p className="xt-[26px]">
                                        Ticket Details
                                    </p>
                                </motion.div>
                                <div id="ticket-modal" className="mt-6 lg:mt-10">
                                    <div className="ticket-card">
                                        <div className="flex gap-4 h-full">
                                            <div className="ticket-main-image min-w-[120px] ">
                                                <img src={showData?.banner} alt="" />
                                            </div>
                                            <div className="flex flex-col pt-5 gap-4 h-full">
                                                <p className="modal-header urbanist">{showData?.name}</p>
                                                <p className="modal-date urbanist">
                                                    {formatDateInCustomTimezone(
                                                        showData?.show_date,
                                                        showData?.show_time
                                                    )}
                                                </p>
                                                <div className="modal-location urbanist">
                                                    <svg
                                                        width="16"
                                                        height="17"
                                                        viewBox="0 0 16 17"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            clipRule="evenodd"
                                                            d="M2.33301 7.823C2.33301 4.75643 4.8956 2.27783 7.99531 2.27783C11.1038 2.27783 13.6663 4.75643 13.6663 7.823C13.6663 9.36829 13.1043 10.8029 12.1793 12.0189C11.1589 13.3602 9.90111 14.5288 8.48537 15.4461C8.16135 15.6581 7.86892 15.6741 7.51331 15.4461C6.0895 14.5288 4.83173 13.3602 3.82001 12.0189C2.89433 10.8029 2.33301 9.36829 2.33301 7.823ZM6.12916 7.99567C6.12916 9.02297 6.96745 9.83095 7.9953 9.83095C9.02384 9.83095 9.87019 9.02297 9.87019 7.99567C9.87019 6.97636 9.02384 6.12905 7.9953 6.12905C6.96745 6.12905 6.12916 6.97636 6.12916 7.99567Z"
                                                            fill="white"
                                                        />
                                                    </svg>
                                                    <span className="modal-date">
                                                        {showData?.venue?.name}, {showData?.venue?.city},{" "}
                                                        {showData?.venue?.state}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <motion.form
                                    initial={{ opacity: 0 }}
                                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                                    transition={{ duration: 0.5, delay: 0.5 }}
                                    action=""
                                    className="mt-7 lg:pl-0 lg:mt-12"
                                >
                                    <div className="">
                                        <div className="form-item mt-4 flex flex-col gap-2">
                                            <div className="flex flex-col lg:flex-row gap-3">

                                                <div className="w-full flex flex-col gap-2">
                                                    <label
                                                        htmlFor="first_name"
                                                        className="text-[12px] lg:text-base text-[#718096]"
                                                    >
                                                        First Name
                                                    </label>
                                                    <input
                                                        className="bg-[#36383F] w-full text-white focus:outline-none text-[16px] lg:text-base rounded-[5px] p-3"
                                                        placeholder="Enter your First Name"
                                                        type="text"
                                                        name="first_name"
                                                        id="first_name"
                                                        {...formik.getFieldProps("first_name")}
                                                        required
                                                    />
                                                    {formik.touched.first_name && formik.errors.first_name ? (
                                                        <div className="text-red-500 text-sm">{formik.errors.first_name}</div>
                                                    ) : null}
                                                </div>

                                                <div className="w-full flex flex-col gap-2">
                                                    <label
                                                        htmlFor="last_name"
                                                        className="text-[12px] lg:text-base text-[#718096]"
                                                    >
                                                        Last Name
                                                    </label>
                                                    <input
                                                        className="bg-[#36383F] w-full text-white focus:outline-none text-[16px] lg:text-base rounded-[5px] p-3"
                                                        placeholder="Enter your Last Name"
                                                        type="text"
                                                        name="last_name"
                                                        id="last_name"
                                                        {...formik.getFieldProps("last_name")}
                                                        required
                                                    />
                                                    {formik.touched.last_name && formik.errors.last_name ? (
                                                        <div className="text-red-500 text-sm">{formik.errors.last_name}</div>
                                                    ) : null}
                                                </div>

                                            </div>
                                        </div>

                                        <div className="form-item mt-4 flex flex-col gap-2 ">
                                            <label
                                                className="text-[12px] lg:text-base text-[#718096]"
                                                htmlFor="email"
                                            >
                                                Email address
                                            </label>
                                            <div className="relative">
                                                <input
                                                    className="bg-[#36383F] w-full text-[#fff] focus:outline-none text-[16px] lg:text-base rounded-[5px] p-3 lg:p-3 "
                                                    placeholder="Enter your e-mail"
                                                    type="email"
                                                    {...formik.getFieldProps("email")}
                                                    id="email"
                                                    required
                                                />
                                                {formik.touched.email && formik.errors.email ? (
                                                    <div className="text-red-500 text-sm">
                                                        {formik.errors.email}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="form-item mt-4 flex flex-col gap-2 ">
                                            <label
                                                className="text-[12px] lg:text-base text-[#718096]"
                                                htmlFor="phone_number"
                                            >
                                                Phone number
                                            </label>
                                            <input
                                                type="hidden"
                                                name="country_code"
                                                value={formik.country_code}
                                            />
                                            <div className="flex w-full gap-2 items-start ">
                                                <div className="lg:w-[48px] lg:h-[48px] ">
                                                    <PhoneInput
                                                        country={"us"}
                                                        value={formik.values.country_code}
                                                        onChange={(value, country) => {
                                                            formik.setFieldValue("country_code", `+${country.dialCode}`);
                                                        }}
                                                        inputStyle={{
                                                            display: "none",
                                                        }}
                                                        buttonStyle={{
                                                            borderRadius: "6px",
                                                            height: "40px",
                                                            border: "1px solid #ccc",
                                                        }}
                                                        dropdownStyle={{
                                                            zIndex: 9999,
                                                        }}
                                                        disableCountryCode={true}
                                                        disableDropdown={false}
                                                        enableAreaCodes={false}
                                                        className="min-w-[38px] detailsCustomInput "
                                                    />
                                                </div>

                                                <input
                                                    type="text"
                                                    name="phone_number"
                                                    placeholder="1234567890"
                                                    className="bg-[#36383F] w-full text-[#fff] focus:outline-none text-[16px] lg:text-base rounded-[5px] p-3 lg:p-3 "
                                                    value={formik.values.phone_number}
                                                    pattern="^[0-9]+$"
                                                    title="Only numbers are allowed"
                                                    onChange={(e) => {
                                                        const cleaned = e.target.value.replace(/[^0-9]/g, "");
                                                        formik.setFieldValue("phone_number", cleaned);
                                                    }} />
                                            </div>

                                            {formik.touched.phone_number && formik.errors.phone_number && (
                                                <div className="text-red-500 text-sm">{formik.errors.phone_number}</div>
                                            )}
                                        </div>
                                    </div>
                                </motion.form>
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                                    transition={{ duration: 0.5, delay: 0.6 }}
                                    className="mt-5 pt-1 relative"
                                >
                                    <div className="mt-6 lg:mt-[50px] flex items-start lg:items-start gap-2">
                                        <input
                                            type="checkbox"
                                            className="custom_checkbox min-w-[24px] mt-2 lg:mt-1"
                                            id="agree"
                                            checked={formik.values.is_sms_on}
                                            onChange={() =>
                                                formik.setFieldValue(
                                                    "is_sms_on",
                                                    !formik.values.is_sms_on
                                                )
                                            }
                                        />
                                        <p className="font-urbanist text-sm lg:text-base text-[#9E9E9E]">
                                            <span>I </span>
                                            <span className="text-white font-[700] mb-0">
                                                consent to receive SMS
                                            </span>
                                            <span>
                                                {" "}
                                                reminders and important event updates about this ticket
                                                purchase.
                                            </span>
                                        </p>
                                    </div>
                                    <div className="mt-3 lg:mt-[50px] flex items-start lg:items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="custom_checkbox mt-2 lg:mt-0"
                                            id="agree"
                                            checked={formik.values.agree}
                                            onChange={() =>
                                                formik.setFieldValue("agree", !formik.values.agree)
                                            }
                                        />
                                        <p className="font-urbanist text-xs lg:text-base text-[#9E9E9E]">
                                            I agree to the{" "}
                                            <a
                                                href="https://help.qoncertapp.com/privacy-policy"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-[700] text-white hover:text-[#fa2cb4]"
                                            >
                                                Privacy
                                            </a>
                                            ,{" "}
                                            <a
                                                href="https://help.qoncertapp.com/terms"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-[700] text-white hover:text-[#fa2cb4]"
                                            >
                                                Terms
                                            </a>{" "}
                                            <span className="text-white ">and </span>
                                            <a
                                                href="https://help.qoncertapp.com/purchase-policy"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-[700] text-white hover:text-[#fa2cb4]"
                                            >
                                                Purchase Terms
                                            </a>
                                        </p>
                                    </div>

                                    {formik.touched.agree && formik.errors.agree ? (
                                        <div className="text-red-500 text-sm">
                                            {formik.errors.agree}
                                        </div>
                                    ) : null}
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                                    transition={{ duration: 0.5, delay: 0.6 }}
                                    className="mt-5 lg:mt-12 pt-1 relative"
                                >
                                    <div className="absolute w-[120%] left-[-10%]">
                                        <svg
                                            width="100%"
                                            height="2"
                                            viewBox="0 0 694 2"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <line y1="1" x2="694.001" y2="1" stroke="#332D41" />
                                        </svg>
                                    </div>
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                                    transition={{ duration: 0.5, delay: 0.8 }}
                                    className="mt-5 lg:mt-5 modal-footer flex flex-col md:flex-row gap-7 items-center justify-end"
                                >
                                    <button onClick={() => { setOpenModal(false); router.back() }} className="border-[#35383F] min-w-max hidden lg:block bg-[#6b6b6b17] font-urbanist font-[500] border text-[14px] lg:text-base py-[6px] lg:py-[19px] lg:leading-[10px] w-[180px] px-[62px] lg:w-[340px] rounded-[10px] ">
                                        Back to Show Details
                                    </button>
                                    <button
                                        id="submit-button-id"
                                        onClick={() => {
                                            if (!isSubmitting) {
                                                formik.handleSubmit();
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        className="main_btn_bg font-[800] min-w-max text-[14px] font-urbanist lg:text-base py-[10px] lg:py-[20px] lg:leading-[10px] px-[62px] w-[179px] lg:w-[340px] rounded-[10px] flex items-center justify-center gap-2 disabled:opacity-50"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <svg
                                                    className="animate-spin h-2 w-2 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                                    ></path>
                                                </svg>
                                                <span>Submitting...</span>
                                            </>
                                        ) : (
                                            "Next : Enter Payment"
                                        )}
                                    </button>
                                </motion.div>
                                <div className="mt-10 text-center">
                                    <div className="font-urbanist flex justify-center text-[14px] lg:text-base text-[#9E9E9E]">
                                        Already have an account?{" "}
                                        <p
                                            onClick={() => router.push("/login")}
                                            className="font-[700] cursor-pointer ml-1 text-white hover:text-[#fa2cb4]"
                                        >
                                            Log In
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div >
            )
            }
        </User >
    );
}